import React from 'react';
import {
  RESENAS, TESTIMONIOS, GOOGLE_RATING, GOOGLE_RESENAS_TOTAL,
  MOSTRAR_TOTAL_RESENAS, GOOGLE_FICHA_URL,
} from '../../data/constants';
import { StarIcon, GoogleIcon } from '../Icons';
import './Resenas.css';

/** Fila de estrellas. Redondea a la media estrella más cercana como Google. */
export const Estrellas: React.FC<{ valor: number; size?: number }> = ({ valor, size = 14 }) => (
  <span className="estrellas" aria-label={`${valor} de 5 estrellas`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className={i < Math.round(valor) ? 'estrella-on' : 'estrella-off'}>
        <StarIcon size={size} />
      </span>
    ))}
  </span>
);

/* Distintivo compacto de la nota de Google. Va en la portada, sobre el pliegue:
   quien llega desde una búsqueda y no nos conoce decide con esto. Si todavía no
   hay reseñas cargadas no se muestra nada, para no inventar una nota. */
export const GoogleRatingBadge: React.FC<{ className?: string }> = ({ className }) => {
  if (!GOOGLE_RATING) return null;
  const contenido = (
    <>
      <GoogleIcon size={15} />
      <strong>{GOOGLE_RATING.toFixed(1)}</strong>
      <Estrellas valor={GOOGLE_RATING} size={12} />
      <span className="rating-badge-count">
        {MOSTRAR_TOTAL_RESENAS ? `${GOOGLE_RESENAS_TOTAL} reseñas en Google` : 'en Google'}
      </span>
    </>
  );
  return GOOGLE_FICHA_URL ? (
    <a
      className={`rating-badge ${className ?? ''}`}
      href={GOOGLE_FICHA_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      {contenido}
    </a>
  ) : (
    <div className={`rating-badge ${className ?? ''}`}>{contenido}</div>
  );
};

const Resenas: React.FC = () => {
  // Sin nada cargado la sección desaparece por completo: preferimos no mostrar
  // nada antes que mostrar opiniones de relleno.
  if (RESENAS.length === 0 && TESTIMONIOS.length === 0) return null;

  return (
    <section id="resenas" style={{ background: 'var(--dark-2)' }}>
      <div className="section-label">Opiniones</div>
      <h2 className="section-title">Lo que dicen <em>nuestros clientes</em></h2>
      <div className="divider" />

      {RESENAS.length > 0 && (
      <>
      <div className="resenas-header">
        <GoogleRatingBadge />
        {GOOGLE_FICHA_URL && (
          <a
            className="resenas-verlas"
            href={GOOGLE_FICHA_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver todas en Google →
          </a>
        )}
      </div>

      <div className="resenas-grid">
        {RESENAS.map((r) => (
          <figure className="resena-card" key={`${r.autor}-${r.fecha}`}>
            <Estrellas valor={r.estrellas} />
            <blockquote className="resena-texto">{r.texto}</blockquote>
            <figcaption className="resena-autor">
              <span className="resena-inicial" aria-hidden="true">{r.autor.charAt(0)}</span>
              <span>
                <span className="resena-nombre">{r.autor}</span>
                <span className="resena-fecha">{r.fecha}</span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
      </>
      )}

      {TESTIMONIOS.length > 0 && (
        <div className="testimonios">
          {TESTIMONIOS.map((t) => (
            <figure className="testimonio-card" key={t.autor}>
              <div className="testimonio-marca">Testimonio de cliente</div>
              <blockquote className="testimonio-texto">
                {t.texto.split('\n\n').map((parrafo) => (
                  <p key={parrafo.slice(0, 24)}>{parrafo}</p>
                ))}
              </blockquote>
              <figcaption className="testimonio-autor">
                <span className="testimonio-nombre">{t.autor}</span>
                <span className="testimonio-materia">{t.materia}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
};

export default Resenas;
